import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import useShortcut from '../../../services/shortcuts/useShortcut';
import s from './infoBubble.module.scss';

/**
 * A small (i) that explains the thing next to it.
 *
 * Hover shows the bubble on a desktop; a click or tap pins it open, which is
 * the only way to read it on a phone, where there is no hover. A click
 * anywhere else, or on the (i) again, closes it. The bubble is in the DOM
 * whether or not it is showing, so a screen reader reaches it through
 * aria-describedby on the button without having to open it.
 *
 * Content is whatever is passed as children - a few short paragraphs is the
 * intended size. Keep the words for what the feature does and does not do;
 * a bubble that repeats the label it sits next to is noise.
 */

interface Props {
  /** What the (i) is for, read by screen readers. */
  label?: string;
  /**
   * Which edge of the (i) the bubble hangs from. Right for an (i) that sits
   * at the right-hand end of something narrow - a context menu item - so the
   * bubble opens back over the menu rather than off the screen.
   */
  align?: 'left' | 'right';
  className?: string;
  children: React.ReactNode;
}

let nextId = 0;

export default function InfoBubble(props: Props) {
  const { label = 'More about this', align = 'left', className, children } = props;

  const [open, setOpen] = useState(false);
  const [id] = useState(() => `info-bubble-${++nextId}`);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  /** Read inside the listeners below, which outlive any one render. */
  const openRef = useRef(open);
  openRef.current = open;

  /**
   * Closing it: a press outside, or Escape.
   *
   * Registered once on mount rather than when it opens. Gating on `open`
   * looked tidier and lost keys: an effect runs after the render that
   * opened the bubble has committed, so an Escape pressed in that window
   * arrived before the listener existed and did nothing. The state is read
   * through a ref instead, so the listeners are in place from the start.
   */
  useEffect(function closeOnPressOutside() {
    function onPointerDown(ev: MouseEvent | TouchEvent) {
      if (!openRef.current) return;

      if (wrapperRef.current && !wrapperRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, []);

  // A bubble sits on top of whatever it explains, a dialog included, and the
  // dialog registered its Escape after this bubble mounted inside it. The
  // priority is what puts an *open* bubble first anyway; closed, it declines
  // and the key goes on to the dialog.
  useShortcut({
    id: 'info-bubble-close',
    keys: { key: 'Escape' },
    group: 'Dialogs',
    description: 'Close the info bubble',
    whileTyping: true,
    whileMenuOpen: true,
    priority: 10,
    listed: false,
    run: () => {
      if (!openRef.current) {
        return false;
      }

      setOpen(false);
    },
  });

  return (
    <span
      ref={wrapperRef}
      className={classNames(s.wrapper, className, {
        [s.open]: open,
        [s.alignRight]: align === 'right',
      })}
    >
      <button
        type="button"
        className={s.trigger}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={id}
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setOpen((x) => !x);
        }}
        onFocus={(ev) => {
          // A keyboard focus opens it. A mouse press is not :focus-visible,
          // and is the click handler's business - opening here too would
          // fight the toggle.
          try {
            if (ev.currentTarget.matches(':focus-visible')) {
              setOpen(true);
            }
          } catch {
            // No :focus-visible in this engine; hover and click still work.
          }
        }}
        onBlur={(ev) => {
          // Not when focus moved into the bubble itself - some of them carry
          // a link, and closing on the way to it would eat the click.
          const next = ev.relatedTarget as Node | null;

          if (!next || !wrapperRef.current || !wrapperRef.current.contains(next)) {
            setOpen(false);
          }
        }}
      >
        i
      </button>
      <span role="tooltip" id={id} className={s.bubble}>
        {children}
      </span>
    </span>
  );
}
